import axios from "axios";
import { DataGrid, LoadPanel } from "devextreme-react";
import AuthFileUploader from "../../../common/AuthFileUploader";
import {
  Button,
  Column,
  ColumnChooser,
  ColumnFixing,
  Editing,
  Export,
  FilterRow,
  Grouping,
  GroupPanel,
  HeaderFilter,
  Item,
  Lookup,
  MasterDetail,
  Paging,
  Popup,
  Position,
  Scrolling,
  SearchPanel,
  Selection,
  Sorting,
  Texts,
  Toolbar,
  ToolbarItem,
} from "devextreme-react/data-grid";
import CustomStore from "devextreme/data/custom_store";
import { useCallback, useRef, useState } from "react";
import styled from "styled-components";
import { vendorType } from "../data";
import PaymentVendorMonthly from "./PaymentVendorMonthly";
import VendorContractForm from "./VendorContractForm";
import { useDispatch, useSelector } from "react-redux";
import { hasPermission } from "../../../../utils/hasPermission";
import { PERM } from "../../../../utils/permissions";
import { init, updateAll } from "./Redux/vendorContract";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import { saveAs } from "file-saver-es";

import "./index.css";
import { notifyMsg } from "../../../common/notify";

const VendorGridWrapper = styled.div`
  #vendorGrid {
    max-width: 99vw;
    max-height: 72vh;
    margin: 16px 0px;
  }
`;

export default function Vendor() {
  const vendorGridRef = useRef(null);
  const projectDropDownBoxRef = useRef(null);
  const formVendorContractRef = useRef(null);

  const vendorContract = useSelector((state) => state.vendorContract);

  const [vendorContractPopupVisible, setVendorContractPopupVisible] =
    useState(false);
  const [loadPanelVisible, setLoadPanelVisible] = useState(false);

  const userInfo = useSelector((state) => state.userInfo);
  const canEditSales = hasPermission(userInfo.permissions, PERM.SALES_MGMT_EDIT);

  const dispatch = useDispatch();

  const [dataSource, setDataSource] = useState(
    new CustomStore({
      key: "id",
      load: async () => {
        // console.debug("[LOAD of EDC]");

        // [R15]
        const result = await axios.get(
          `${process.env.REACT_APP_BECCO_API_URL}/api/salesMgmt/payt/vendor/contr`
        );

        return result.data;
      },
      insert: async (values) => {
        // console.debug("[insert]");

        await axios.post(
          `${process.env.REACT_APP_BECCO_API_URL}/api/salesMgmt/payt/vendor/${values.contrNo}/${values.name}/${values.type}`,
          values
        );
      },
      update: async (key, values) => {
        await axios.put(
          `${process.env.REACT_APP_BECCO_API_URL}/api/salesMgmt/payt/vendor/${key.id}`,
          values
        );
      },
      remove: async (key) => {
        await axios.delete(
          `${process.env.REACT_APP_BECCO_API_URL}/api/salesMgmt/payt/vendor/${key}`
        );
      },
    })
  );

  const contentRender = useCallback(() => {
    return (
      <VendorContractForm
        projectDropDownBoxRef={projectDropDownBoxRef}
        vendorContract={vendorContract}
        formVendorContractRef={formVendorContractRef}
      />
    );
  }, [vendorContract]);

  const onSaveClick = useCallback(() => {
    // console.debug("[onSaveClick]");

    let valid = true;

    if (!vendorContract.contrNo) {
      projectDropDownBoxRef.current.instance.option(
        "validationStatus",
        "invalid"
      );

      valid = false;
    }

    if (valid && formVendorContractRef.current.instance.validate().isValid) {
      setVendorContractPopupVisible(false);

      if (vendorContract.action == "insert") {
        vendorGridRef.current.instance
          .getDataSource()
          .store()
          .insert(vendorContract)
          .then(() => {
            vendorGridRef.current.instance.refresh();
          });
      } else {
        const updateValues = {};

        for (const key of vendorContract.updateFields) {
          if (vendorContract.hasOwnProperty(key)) {
            updateValues[key] = vendorContract[key];
          }
        }

        vendorGridRef.current.instance
          .getDataSource()
          .store()
          .update(
            {
              id: vendorContract.id,
            },
            updateValues
          )
          .then(() => {
            vendorGridRef.current.instance.refresh();
          });
      }
    }
  }, [vendorContract]);

  const onInitNewRow = () => {
    // console.debug("[onInitNewRow]");

    dispatch(init());

    setVendorContractPopupVisible(true);
  };

  const onEditingStart = (e) => {
    // console.debug("[onEditingStart]");

    dispatch(
      updateAll({
        ...e.data,
        action: "update",
      })
    );

    setVendorContractPopupVisible(true);
  };

  const onCellPrepared = (e) => {
    if (e.rowType == "header") {
      e.cellElement.style.fontWeight = "bold";
      e.cellElement.style.textAlign = "center";
      e.cellElement.style.backgroundColor = "#E2EFDA";

      switch (e.column.dataField) {
        case "WODate":
        case "startDate":
        case "endDate":
          e.cellElement.style.backgroundColor = "#FFFF00";
          break;
        case "invCount":
          e.cellElement.style.backgroundColor = "#808080";
          break;
      }
    }
  };

  const onExporting = (e) => {
    const groupedColumns = e.component
      .getVisibleColumns()
      .filter((c) => c.groupIndex > -1);

    if (groupedColumns.length > 0) {
      notifyMsg("Ungroup All.", "error");

      e.cancel = true;

      return;
    }

    vendorGridRef.current.instance.clearFilter();

    e.component.beginUpdate();
    e.component.columnOption("id", "visible", true);

    const workbook = new Workbook();

    workbook.creator = "BeTree";
    workbook.created = new Date();

    const worksheet = workbook.addWorksheet("Vendor Usage");

    worksheet.properties.defaultRowHeight = 13.5;

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: ({ gridCell, excelCell }) => {
        excelCell.font = {
          size: 10,
        };

        excelCell.border = {
          top: {
            style: "thin",
          },
          left: {
            style: "thin",
          },
          bottom: {
            style: "thin",
          },
          right: {
            style: "thin",
          },
        };

        if (gridCell.rowType === "data") {
          switch (gridCell.column.dataField) {
            case "name":
            case "contrNo":
            case "type":
            case "product":
            case "WO Date":
            case "MSA":
            case "contrPeriod":
            case "invCount":
            case "startDate":
            case "endDate":
              excelCell.alignment = {
                vertical: "middle",
                horizontal: "right",
              };
              break;
            case "unitCost":
            case "sum":
              if (gridCell.value) {
                excelCell.value = parseInt(gridCell.value, 10);
                excelCell.numFmt = "#0,000";
              }
              break;
            case "remark":
              excelCell.alignment = {
                wrapText: true,
              };
              break;
            case "id":
              excelCell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FBFBFBF" },
              };
              break;
          }
        } else if (gridCell.rowType == "header") {
          excelCell.font = {
            bold: true,
            size: 10,
          };

          excelCell.alignment = {
            vertical: "middle",
            horizontal: "center",
          };

          switch (gridCell.column.dataField) {
            case "name":
            case "contrNo":
            case "type":
            case "product":
            case "MSA":
            case "unitCost":
            case "contrPeriod":
            case "remark":
            case "sum":
              excelCell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: {
                  argb: "FE2EFDA",
                },
              };
              break;
            case "invCount":
              excelCell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: {
                  argb: "F808080",
                },
              };
              break;
            case "WODate":
            case "startDate":
            case "endDate":
              excelCell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: {
                  argb: "FFFFF00",
                },
              };
              break;
          }
        }
      },
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], {
            type: "application/octet-stream",
          }),
          "Vendor.xlsx"
        );
      });

      e.component.columnOption("id", "visible", false);
      e.component.endUpdate();

      notifyMsg("Vendor 파일이 다운르도 되었습니다.", "success");
    });
  };

  const onUploadStarted = (e) => {
    setLoadPanelVisible(true);
  };

  const onUploaded = (e) => {
    setLoadPanelVisible(false);

    notifyMsg("Vendor 파일이 업로드되었습니다.", "success");

    vendorGridRef.current.instance.refresh();
  };

  const onUploadError = (e) => {
    setLoadPanelVisible(false);

    if (e.error.response) {
      notifyMsg(JSON.parse(e.error.response).message, "error");
    } else {
      notifyMsg("에러가 발생했습니다. 관리자에게 문의해주세요.", "error");
    }
  };

  const onHiding = () => {
    setLoadPanelVisible(false);
  };

  return (
    <VendorGridWrapper>
      <DataGrid
        id="vendorGrid"
        ref={vendorGridRef}
        dataSource={dataSource}
        allowColumnReordering={true}
        allowColumnResizing={true}
        columnResizingMode="widget"
        rowAlternationEnabled={true}
        showBorders={true}
        showRowLines={true}
        showColumnLines={true}
        width={"100%"}
        height={"100%"}
        repaintChangesOnly={true}
        highlightChanges={true}
        columnAutoWidth={true}
        wordWrapEnabled={true}
        onInitNewRow={onInitNewRow}
        onEditingStart={onEditingStart}
        onCellPrepared={onCellPrepared}
        onExporting={onExporting}
      >
        <GroupPanel visible={true} />
        <SearchPanel visible={true} highlightCaseSensitive={true} width={240} />
        <Grouping autoExpandAll={true} contextMenuEnabled={true} />
        <Scrolling
          mode={"virtual"}
          rowRenderingMode={"virtual"}
          columnRenderingMode={"virtual"}
          showScrollbar={"always"}
        />
        <HeaderFilter visible={true} />
        <FilterRow visible={true} applyFilter={"auto"} />
        <Paging enabled={false} />
        <Sorting mode={"multiple"} />
        <Selection mode={"single"} />
        <ColumnChooser height={"340px"} enabled={true} mode={"dragAndDrop"}>
          <Position
            my={"right top"}
            at={"right bottom"}
            of={".dx-datagrid-column-chooser-button"}
          />
        </ColumnChooser>

        <ColumnFixing enabled={true} />

        <Export enabled={true} />

        <Editing
          refreshMode={"reshape"}
          mode={"popup"}
          allowAdding={true}
          allowUpdating={true}
          allowDeleting={true}
        >
          <Texts
            confirmDeleteMessage={"Are you sure want to delete this contract?"}
          />
          <Popup
            visible={vendorContractPopupVisible}
            showTitle={true}
            title={"Vendor Contract"}
            height={"auto"}
            showCloseButton={true}
            contentRender={contentRender}
          >
            <ToolbarItem
              widget={"dxButton"}
              toolbar={"top"}
              location={"after"}
              options={{
                icon: "save",
                type: "normal",
                stylingmode: "contained",
                onClick: onSaveClick,
              }}
            />
          </Popup>
        </Editing>

        <Column
          dataField={"id"}
          caption={"Id"}
          dataType={"number"}
          visible={false}
        />

        <Column
          dataField={"name"}
          // groupIndex={1}
          caption={"Vendor Name"}
          dataType={"string"}
        />
        <Column
          dataField={"contrNo"}
          caption={"Contract No."}
          dataType={"string"}
          width={140}
        />
        <Column
          dataField={"type"}
          caption={"Type"}
          dataType={"string"}
          width={80}
        >
          <Lookup
            dataSource={vendorType}
            displayExpr={"name"}
            valueExpr={"id"}
          />
        </Column>
        <Column dataField={"product"} caption={"Product"} dataType={"string"} />
        <Column
          dataField={"WODate"}
          caption={"WO Date"}
          dataType={"date"}
          format={"yyyy-MM-dd"}
          width={100}
        />
        <Column dataField={"MSA"} caption={"MSA"} dataType={"string"} />
        <Column
          dataField={"unitCost"}
          caption={"Unit Cost"}
          dataType={"number"}
          format={"#0,000"}
          width={150}
        />
        <Column
          dataField={"contrPeriod"}
          caption={"Contract Period"}
          dataType={"number"}
          width={135}
        />
        <Column
          dataField={"invCount"}
          caption={"Invoice Count"}
          dataType={"number"}
          width={125}
        />
        <Column
          dataField={"startDate"}
          caption={"Start Date"}
          dataType={"date"}
          format={"yyyy-MM-dd"}
          width={100}
        />
        <Column
          dataField={"endDate"}
          caption={"End Date"}
          dataType={"date"}
          format={"yyyy-MM-dd"}
          width={100}
        />
        <Column dataField={"remark"} caption={"Remark"} dataType={"string"} />
        <Column
          dataField={"sum"}
          caption={"Sum"}
          dataType={"number"}
          format={"#0,000"}
          width={150}
        />
        <Column type={"buttons"} allowHiding={false} width={100}>
          <Button name="edit" />
          <Button name="delete" />
        </Column>

        <Toolbar>
          <Item location={"before"} name={"groupPanel"} />

          {canEditSales && (
            <Item location={"before"}>
              <div className="file-uploader-block">
                <AuthFileUploader
                  id="vendorFileUplaoder"
                  width={500}
                  uploadMode="instantly"
                  uploadUrl={`${process.env.REACT_APP_BECCO_API_URL}/api/salesMgmt/payt/vendor?employeeNo=${userInfo.employeeNo}`}
                  accept=".xlsx"
                  allowedFileExtensions={[".xlsx"]}
                  showFileList={false}
                  onUploadStarted={onUploadStarted}
                  onUploaded={onUploaded}
                  onUploadError={onUploadError}
                />
              </div>
            </Item>
          )}

          {canEditSales && (
            <Item location={"after"} name={"addRowButton"} />
          )}
          {canEditSales && (
            <Item location={"after"} name={"exportButton"} />
          )}
          <Item name={"searchPanel"} />
        </Toolbar>

        <MasterDetail
          enabled={true}
          component={(data) => {
            return (
              <PaymentVendorMonthly
                data={data.data}
                vendorGridRef={vendorGridRef}
              />
            );
          }}
        />
      </DataGrid>
      <LoadPanel
        shadingColor="rgba(0, 0, 0, 0.4"
        position={{
          of: "#vendorGrid",
        }}
        visible={loadPanelVisible}
        showIndicator={true}
        shading={true}
        showPane={true}
        onHiding={onHiding}
      />
    </VendorGridWrapper>
  );
}
